import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Search, PackageOpen, Heart, Flame, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { zl } from "@/lib/shop";
import { shopApi, useShopAuth, errText } from "@/lib/shopAuth";
import ShopLayout from "./ShopLayout";
import { HeroSlider } from "./HeroSlider";
import tshirtImg from "@/assets/groups/tshirt.png";
import poloImg from "@/assets/groups/polo.png";
import polaryImg from "@/assets/groups/polary.png";
import kurtkiImg from "@/assets/groups/kurtki.png";
import czapkiImg from "@/assets/groups/czapki.png";
import bluzyImg from "@/assets/groups/bluzy.png";
import inneImg from "@/assets/groups/inne.png";

const GROUP_TILES = [
  { name: "T-SHIRT'Y", img: tshirtImg },
  { name: "POLO", img: poloImg },
  { name: "POLARY", img: polaryImg },
  { name: "KURTKI", img: kurtkiImg },
  { name: "CZAPKI", img: czapkiImg },
  { name: "BLUZY", img: bluzyImg },
  { name: "INNE", img: inneImg },
];
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ShopHome() {
  const navigate = useNavigate();
  const { customer, refresh } = useShopAuth();
  const [data, setData] = useState({ products: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState("new");
  const [favs, setFavs] = useState([]);
  const [slides, setSlides] = useState([]);
  const [heroInterval, setHeroInterval] = useState(6000);
  const [sections, setSections] = useState({ best: [], newest: [] });

  useEffect(() => { setFavs(customer?.favorites || []); }, [customer]);

  useEffect(() => {
    api.get("/shop/hero").then((r) => {
      const d = r.data || {};
      const list = (d.slides || []).map((s) => ({ src: s.file_id ? `${API}/files/${s.file_id}` : s.url, alt: s.alt, href: s.href }));
      setSlides(list);
      setHeroInterval((d.interval_sec || 6) * 1000);
    }).catch(() => setSlides([]));
    api.get("/shop/products").then((r) => {
      const all = r.data?.products || [];
      const best = all.filter((p) => p.bestseller).slice(0, 8);
      const newest = all.filter((p) => !p.bestseller).slice(0, 8);
      setSections({ best, newest });
    }).catch(() => {});
  }, []);

  const toggleFav = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    if (!customer) { toast.info("Zaloguj się, aby zapisywać ulubione"); navigate("/sklep/logowanie"); return; }
    setFavs((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
    try { const { data: d } = await shopApi.post("/shop/auth/favorite", { product_id: id }); setFavs(d.favorites); refresh(); }
    catch (err) { toast.error(errText(err)); setFavs(customer?.favorites || []); }
  };

  useEffect(() => {
    setLoading(true);
    const params = { sort };
    if (q) params.q = q;
    if (cat !== "all") params.type = cat;
    api.get("/shop/products", { params }).then((r) => setData(r.data || { products: [], categories: [] }))
      .catch(() => setData({ products: [], categories: [] })).finally(() => setLoading(false));
  }, [q, cat, sort]);

  const Card = (p) => {
    const promo = Boolean(p.on_promo);
    return (
      <Link key={p.id} to={`/sklep/produkt/${p.id}`} data-testid={`shop-product-${p.id}`}
        className="group rounded-2xl border border-white/10 bg-white/5 overflow-hidden hover:border-amber-400/50 transition-colors">
        <div className="relative aspect-square bg-white overflow-hidden">
          {p.photos?.[0]
            ? <img src={`${API}/files/${p.photos[0]}`} alt={p.name} className="h-full w-full object-contain p-3 group-hover:scale-105 transition-transform duration-300" />
            : <div className="grid h-full place-items-center text-neutral-300"><PackageOpen className="h-10 w-10" /></div>}
          {p.brand_logo && <img src={`${API}/files/${p.brand_logo}`} alt="" className="absolute bottom-2.5 left-2.5 z-10 h-7 w-auto max-w-[40%] object-contain" />}
          {p.bestseller && <span className="absolute top-3 left-3 rounded-full bg-amber-400 px-2.5 py-1 text-[11px] font-bold text-[#0D1117]">BESTSELLER</span>}
          {promo && <span className={`absolute left-3 rounded-full bg-rose-500 px-2.5 py-1 text-[11px] font-bold text-white ${p.bestseller ? "top-11" : "top-3"}`}>PROMOCJA</span>}
          <button type="button" data-testid={`fav-btn-${p.id}`} onClick={(e) => toggleFav(e, p.id)} aria-label="Dodaj do ulubionych"
            className="absolute top-2.5 right-2.5 grid h-9 w-9 place-items-center rounded-full bg-black/55 backdrop-blur-sm transition-transform hover:scale-110 active:scale-95">
            <Heart className={`h-4.5 w-4.5 transition-colors ${favs.includes(p.id) ? "fill-amber-400 text-amber-400" : "text-white"}`} />
          </button>
        </div>
        <div className="p-4">
          {p.category && <p className="text-[11px] uppercase tracking-wider text-amber-400/80">{p.category}</p>}
          <p className="mt-1 font-semibold leading-snug line-clamp-2 min-h-[2.6em]">{p.name}</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-lg font-extrabold">{zl(p.price_grosz)}</span>
            {promo && p.regular_grosz ? <span className="text-sm text-neutral-500 line-through">{zl(p.regular_grosz)}</span> : null}
          </div>
          {promo && <p className="mt-0.5 text-[10px] text-neutral-500">{p.promo_basis === "since_launch" ? "Najniższa cena od startu sprzedaży" : "Najniższa cena z 30 dni przed obniżką"}: {zl(p.promo_reference_grosz)}</p>}
        </div>
      </Link>
    );
  };

  const Row = ({ icon: Icon, title, accent, items, testid }) => items.length === 0 ? null : (
    <section className="mb-10" data-testid={testid}>
      <div className="mb-4 flex items-center gap-2">
        <Icon className={`h-5 w-5 ${accent}`} />
        <h2 className="font-display text-xl sm:text-2xl font-extrabold uppercase tracking-tight">{title}</h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">{items.map(Card)}</div>
    </section>
  );

  const showSections = !q && cat === "all" && (sections.best.length + sections.newest.length) >= 4;

  return (
    <ShopLayout>
      <HeroSlider slides={slides} interval={heroInterval} />

      <section className="mx-auto max-w-7xl px-4 pt-10" data-testid="shop-realizations-groups">
        <div className="text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">Zobacz nasze <span className="text-amber-400">realizacje</span></h2>
          <p className="mt-2 text-sm text-neutral-400">Wybierz grupę produktów i zobacz nasze wykonane realizacje.</p>
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4">
          {GROUP_TILES.map((g) => (
            <button key={g.name} data-testid={`realizacje-group-${g.name}`} onClick={() => navigate(`/sklep/realizacje?grupa=${encodeURIComponent(g.name)}`)}
              className="group overflow-hidden rounded-2xl border border-white/10 bg-white transition-all hover:border-amber-400/60 hover:-translate-y-1">
              <div className="aspect-square overflow-hidden bg-white p-2">
                <img src={g.img} alt={g.name} loading="lazy" className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105" />
              </div>
              <span className="block bg-[#0D1117] py-2.5 text-center text-xs sm:text-sm font-extrabold uppercase tracking-wider">{g.name}</span>
            </button>
          ))}
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8">
        {showSections && (
          <>
            <Row icon={Flame} title="Bestsellery" accent="text-amber-400" items={sections.best} testid="shop-section-bestsellers" />
            <Row icon={Sparkles} title="Nowości" accent="text-sky-400" items={sections.newest} testid="shop-section-newest" />
          </>
        )}

        <div className="mb-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
            <Input data-testid="shop-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj produktu…"
              className="pl-9 bg-white/5 border-white/10 text-neutral-100 placeholder:text-neutral-500" />
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger data-testid="shop-filter-cat" className="w-full sm:w-52 bg-white/5 border-white/10"><SelectValue placeholder="Kategoria" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Wszystkie kategorie</SelectItem>{data.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="shop-sort" className="w-full sm:w-44 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="new">Najnowsze</SelectItem><SelectItem value="price_asc">Cena rosnąco</SelectItem><SelectItem value="price_desc">Cena malejąco</SelectItem></SelectContent>
          </Select>
        </div>

        {showSections && <h2 className="mb-4 font-display text-xl sm:text-2xl font-extrabold uppercase tracking-tight">Wszystkie produkty</h2>}

        {loading ? (
          <div className="grid place-items-center py-20 text-neutral-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : data.products.length === 0 ? (
          <div className="grid place-items-center py-20 text-neutral-500" data-testid="shop-empty"><PackageOpen className="h-10 w-10 mb-3" /><p>Brak produktów spełniających kryteria.</p></div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="shop-product-grid">
            {data.products.map(Card)}
          </div>
        )}
      </div>
    </ShopLayout>
  );
}
